import { useEffect, useRef, useState } from 'react'
import { fetchSearchPrices } from '../api/getSearchPrices'

const mergePricesChunkByMaterialId = (previous, chunk) => {
  const existingPrices = Array.isArray(previous.prices) ? previous.prices : []
  const nextPricesByMaterialId = new Map()

  for (const item of existingPrices) {
    if (!item?.materialId) continue
    nextPricesByMaterialId.set(item.materialId, item)
  }

  for (const item of chunk.prices) {
    if (!item?.materialId) continue
    nextPricesByMaterialId.set(item.materialId, item)
  }

  return {
    ...previous,
    ...chunk,
    prices: Array.from(nextPricesByMaterialId.values()),
  }
}

/**
 * Async price-data hook.
 *
 * When `products` changes (i.e., Coveo returns a new result page):
 *  1. Cancels any in-flight SSE price stream from the previous query
 *  2. Resets price state
 *  3. Opens a new SSE connection and collects `prices` events
 *
 * Returns the same shape that the rest of the app already expects:
 *   { priceInfo, searchSSEComplete, searchSSEError }
 *
 * @param {Array}  products   - Mapped product objects (must have .materialId)
 * @param {object} priceContext
 * @param {string} priceContext.salesOrg
 * @param {string} [priceContext.contractId]
 * @param {string} [priceContext.contractType]
 */
export function usePriceData(products, { salesOrg, contractId, contractType } = {}) {
  const [priceInfo, setPriceInfo] = useState(null)
  const [searchSSEComplete, setSSEComplete] = useState(false)
  const [searchSSEError, setSSEError] = useState(false)
  const requestKey = [(products || []).map((p) => p.materialId).join(','), salesOrg, contractId, contractType].join('|')
  const [activeKey, setActiveKey] = useState(null)

  // Keep a stable ref to the cancel function so we can abort on cleanup
  const cancelRef = useRef(null)

  const normalizeMaterialId = (materialId) => {
    if (Array.isArray(materialId)) {
      const first = materialId.find((id) => id != null && `${id}`.trim() !== '')
      return first != null ? `${first}` : ''
    }
    return materialId != null ? `${materialId}` : ''
  }

  useEffect(() => {
    if (!products?.length) return

    // Cancel the previous SSE stream
    cancelRef.current?.()
    cancelRef.current = null

    // Reset price state for the new result set
    setPriceInfo(null)
    setSSEComplete(false)
    setSSEError(false)
    setActiveKey(requestKey)

    const materialIds = products
      .map((p) => normalizeMaterialId(p.materialId))
      .filter(Boolean)

    let mounted = true

    fetchSearchPrices({
      materialIds,
      salesOrg,
      contractId,
      contractType,
      onPrices: (chunk) => {
        if (!mounted) return
        // Preserve previously received `prices` arrays and merge by materialId
        // so batched updates do not drop earlier rows or create stale duplicates.
        setPriceInfo((prev) => {
          const previous = prev || {}
          if (Array.isArray(chunk?.prices)) {
            return mergePricesChunkByMaterialId(previous, chunk)
          }
          return { ...previous, ...chunk }
        })
      },
      onComplete: () => {
        if (mounted) setSSEComplete(true)
      },
      onError: () => {
        if (mounted) setSSEError(true)
      },
    }).then((cancel) => {
      // Store cancel so a subsequent effect run can abort this stream
      if (mounted) cancelRef.current = cancel
      else cancel?.()
    })

    return () => {
      mounted = false
      cancelRef.current?.()
      cancelRef.current = null
    }
    // Re-run whenever the product list identity changes.
    // Comparing by join of materialIds avoids unnecessary re-fires on
    // shallow re-renders where the array reference changes but contents don't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey])
  // Until the effect resets state for a new request, don't expose the previous request's results
  const isCurrent = activeKey === requestKey
  return {
    priceInfo: isCurrent ? priceInfo : null,
    searchSSEComplete: isCurrent && searchSSEComplete,
    searchSSEError: isCurrent && searchSSEError,
  }
}
